/**
 * explosionVfx.ts
 * Pooled sprite-based explosion VFX shared across game scenes.
 *
 * Owns the runtime state for a fixed-size pool of 6-frame burst
 * explosions backed by `src/assets/sprites/explosion-sheet.png` (shipped
 * in PR #60). This module is asset-agnostic on purpose — it tracks per-
 * instance position and elapsed time only. The renderer (next slice)
 * imports the asset module ID, reads {@link getExplosionFrameIndex} for
 * each active instance, and draws the appropriate 32×32 cell.
 *
 * Pool semantics:
 *  - {@link spawnExplosion} acquires the first inactive slot. If the
 *    pool is saturated the spawn is silently dropped — visual polish
 *    must never block gameplay (AGENTS.md pool cap rule).
 *  - {@link stepExplosions} advances every active instance by `dt` and
 *    deactivates any whose elapsed time has passed the total animation
 *    duration ({@link EXPLOSION_FRAME_COUNT} ×
 *    {@link EXPLOSION_FRAME_DURATION_S}).
 *  - {@link resetExplosionVfx} clears every slot. Call on scene change
 *    so a long-lived pool doesn't leak burst frames into the next
 *    scene's first render.
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * Phase 4 — World 2 (enemy death VFX). Engine-side only; the sprite
 * draw path lands in the follow-up PR (`feat/explosion-vfx-render`).
 *
 * @module explosionVfx
 */

/** Number of frames in `explosion-sheet.png`. Locked to the asset layout. */
export const EXPLOSION_FRAME_COUNT = 6;

/**
 * Seconds each explosion frame is displayed. 80 ms × 6 = 480 ms total
 * burst — short enough not to clutter the screen during peak combat,
 * long enough to read as a satisfying kill confirmation.
 */
export const EXPLOSION_FRAME_DURATION_S = 0.08;

/** Total animation duration in seconds. Derived constant. */
export const EXPLOSION_TOTAL_DURATION_S =
  EXPLOSION_FRAME_COUNT * EXPLOSION_FRAME_DURATION_S;

/**
 * Maximum simultaneously active explosions. AGENTS.md caps "particles
 * and hit effects" pools at 50; 16 covers worst-case Level 2-4 (3-wide
 * fighter row + scout fleet + bomber dies in the same tick) with room
 * to spare.
 */
export const EXPLOSION_POOL_SIZE = 16;

/** Frame cell width in logical px (matches the sprite-sheet layout). */
export const EXPLOSION_FRAME_WIDTH = 32;

/** Frame cell height in logical px (matches the sprite-sheet layout). */
export const EXPLOSION_FRAME_HEIGHT = 32;

/**
 * One pooled explosion instance. Fields are public so the renderer can
 * read them without allocating a copy. Mutate only via the functions in
 * this module.
 */
export interface ExplosionInstance {
  /** Centre-X in world space (px). The renderer offsets by frame size. */
  x: number;
  /** Centre-Y in world space (px). */
  y: number;
  /** Seconds since `spawnExplosion()`. Drives the frame index. */
  elapsed: number;
  /** `true` between `spawn` and natural expiry; `false` while pooled. */
  active: boolean;
}

/**
 * Build a fresh, fully-inactive pool. Module-private factory; tests
 * reach for {@link resetExplosionVfx} when they want a clean slate.
 */
function createPool(): ExplosionInstance[] {
  const pool: ExplosionInstance[] = [];
  for (let i = 0; i < EXPLOSION_POOL_SIZE; i++) {
    pool.push({ x: 0, y: 0, elapsed: 0, active: false });
  }
  return pool;
}

/** Singleton pool. Module-scoped so renderer + engine share one source. */
const pool: ExplosionInstance[] = createPool();

/**
 * Acquire an explosion at `(x, y)`. Silently drops on a full pool — VFX
 * are never load-bearing on gameplay correctness, so a saturated pool
 * is preferable to evicting an in-flight burst mid-render.
 *
 * @param x - World-space centre X for the burst.
 * @param y - World-space centre Y for the burst.
 * @returns The acquired instance, or `null` if the pool is saturated.
 */
export function spawnExplosion(x: number, y: number): ExplosionInstance | null {
  for (let i = 0; i < pool.length; i++) {
    const inst = pool[i];
    if (!inst.active) {
      inst.x = x;
      inst.y = y;
      inst.elapsed = 0;
      inst.active = true;
      return inst;
    }
  }
  return null;
}

/**
 * Advance every active explosion by one fixed timestep. Deactivates any
 * instance whose elapsed time has passed the total burst duration.
 *
 * @param dt - Fixed timestep in seconds.
 * @returns Nothing; pool slots are mutated in place.
 */
export function stepExplosions(dt: number): void {
  for (let i = 0; i < pool.length; i++) {
    const inst = pool[i];
    if (!inst.active) continue;
    inst.elapsed += dt;
    if (inst.elapsed >= EXPLOSION_TOTAL_DURATION_S) {
      inst.active = false;
    }
  }
}

/**
 * Frame index (0 .. {@link EXPLOSION_FRAME_COUNT} − 1) for a given
 * elapsed time. Clamped at both ends so a slightly-past-end elapsed
 * value (one tick of slop before deactivation) doesn't index the
 * sprite sheet out-of-bounds.
 *
 * @param elapsed - Seconds since the burst was spawned.
 * @returns Integer frame index into the 6-frame explosion sprite sheet.
 */
export function getExplosionFrameIndex(elapsed: number): number {
  if (elapsed <= 0) return 0;
  const idx = Math.floor(elapsed / EXPLOSION_FRAME_DURATION_S);
  if (idx >= EXPLOSION_FRAME_COUNT) return EXPLOSION_FRAME_COUNT - 1;
  return idx;
}

/**
 * Read-only view of the pool. Callers must not mutate the returned
 * array or its instances — use {@link spawnExplosion} /
 * {@link stepExplosions} / {@link resetExplosionVfx} instead.
 *
 * Returns the full pool (including inactive slots) so the renderer can
 * skip on `inst.active === false` without allocating a filtered copy
 * on every frame.
 *
 * @returns The shared pool array; treat as immutable.
 */
export function getExplosionPool(): readonly ExplosionInstance[] {
  return pool;
}

/**
 * Reset every pool slot to the inactive defaults. Call on scene change
 * so a burst spawned in the old scene doesn't carry over into the new
 * one's first render.
 *
 * @returns Nothing; pool slots are mutated in place.
 */
export function resetExplosionVfx(): void {
  for (let i = 0; i < pool.length; i++) {
    const inst = pool[i];
    inst.x = 0;
    inst.y = 0;
    inst.elapsed = 0;
    inst.active = false;
  }
}
